import { ThemeEvents, CartAddEvent } from '@theme/events';
import { productPage, notify, isAvailable } from '@theme/voyado-client';

// Theme → Voyado Elevate notifications not owned by a component. Loaded only when Voyado is the provider.

// Add-to-cart, site-wide. The grid self-notifies with its ticket; other sources have none, and
// notify.addToCart needs a ticket — so resolve one via productPage. Quantity edits are CartUpdateEvent (ignored).
document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
  if (!(event instanceof CartAddEvent)) return;

  const detail = /** @type {any} */ (event).detail;
  const data = detail?.data ?? {};
  if (data.didError) return;
  if (data.source === 'voyado-grid') return;

  ensureElevateCartAttributes();

  const { productKey, variantKey } = idsFromCartAddEvent(detail);
  if (!productKey) return;

  try {
    const response = await productPage({ productKey });
    const ticket = ticketFor(response, variantKey);
    if (ticket) notify('addToCart', ticket);
  } catch (error) {
    console.warn('[voyado] add-to-cart notify failed', error);
  }
});

// Favorites. The wishlist heart dispatches apa:wishlist:count with { productId, inWishlist } per toggle.
document.addEventListener('apa:wishlist:count', (event) => {
  const detail = /** @type {any} */ (event).detail ?? {};
  const productKey = detail.productId ? String(detail.productId) : '';
  if (!productKey || typeof detail.inWishlist !== 'boolean') return;

  notify(detail.inWishlist ? 'addFavorite' : 'removeFavorite', productKey);
});

/**
 * Stamp the Elevate session context onto the cart so a purchase can be attributed server-side.
 * The Elevate app does NOT set these — confirmed empty cart.attributes — so the theme must.
 * Idempotent and fire-and-forget; safe to re-run on every add.
 */
async function ensureElevateCartAttributes() {
  if (!isAvailable()) return;
  try {
    const context = /** @type {any} */ (window).elevate.query.c;
    const { customerKey, sessionKey } = (await context.session()) ?? {};
    const market = context.market;
    if (!customerKey || !sessionKey || !market) return;
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { _elevate_customer_key: customerKey, _elevate_session_key: sessionKey, _elevate_market: market },
      }),
    });
  } catch (error) {
    console.warn('[voyado] failed to stamp cart attributes', error);
  }
}

/**
 * Resolve the add-to-cart ticket from a productPage response (a `tickets` map keyed by variant key).
 * @param {any} response
 * @param {string} variantKey
 * @returns {string | null}
 */
function ticketFor(response, variantKey) {
  const tickets = response?.tickets;
  if (!tickets) return null;
  return tickets[variantKey] || Object.values(tickets)[0] || null;
}

/**
 * Shopify product/variant id from a CartAddEvent (variant id in `sourceId`, product id in `data.productId`).
 * @param {any} detail
 * @returns {{ productKey: string, variantKey: string }}
 */
function idsFromCartAddEvent(detail) {
  const data = detail?.data ?? {};
  const sourceId = String(detail?.sourceId ?? '');
  return {
    productKey: data.productId ? String(data.productId) : '',
    variantKey: String(data.variantId ?? (/^\d+$/.test(sourceId) ? sourceId : '')),
  };
}
